import { sanitizeHtml } from "utils/sanitizeHtml";
import { FC, useState } from "react";
import { isUsableUrl } from "utils/isUsableUrl";

export interface SectionHeroProps {
  className?: string;
  aboutUs: any;
}

const SectionAbout: FC<SectionHeroProps> = ({ className = "", aboutUs }) => {
  const { image_url, heading, description, width, height, alignment } = aboutUs;
  const [expanded, setExpanded] = useState(false);
  const safeDescription = description || "";
  const isTruncated = safeDescription.length > 500;
  const shortDescription = isTruncated
    ? safeDescription.slice(0, 500)
    : safeDescription;
  const [imgLoaded, setImgLoaded] = useState(true);

  // FMS often leaves width/height empty — fall back to a responsive frame
  // instead of emitting `undefinedpx` (which CSS silently drops, leaving the
  // image at its raw natural size and blowing the layout up).
  const imgStyle: React.CSSProperties = {};
  if (width) imgStyle.width = `${width}px`;
  if (height) imgStyle.height = `${height}px`;

  // 1NOW About Us layout variants (chosen in the FMS Edit Website panel,
  // persisted on Section.alignment). Mirrors Design B's HomeAboutUs:
  //   "left"   → image on the left,  copy on the right
  //   "right"  → image on the right, copy on the left
  //   "center" → single column, image on top, copy centered below
  // Any unset / null / unknown value falls through to the historical Design A
  // render: image left (DOM-first, flex-row), copy right. That legacy layout
  // is byte-for-byte identical to image-left, so we only branch off when the
  // alignment is explicitly "right" or "center".
  const isCenter = alignment === "center";
  const isRight = alignment === "right";
  const isLeft = alignment === "left";
  // image-left is both the explicit "left" case AND the unset/unknown default
  // (the legacy Design A layout already rendered image-first / copy-right).

  // Stat chips + floating badge come from the about-us keypoints, split by
  // `keypoint_type` (same convention as Design B's HomeAboutUs):
  //   keypoint_type="stat"  → heading=value (e.g. "50+"), description=label
  //   keypoint_type="badge" → heading=label (e.g. "Members Only Since 2015")
  // Both areas hide when empty so the layout stays clean if unconfigured.
  // Previously Design A's About never rendered keypoints at all (1NOW-1243).
  const aboutKeypoints: any[] = aboutUs?.keypoints || [];
  const stats = aboutKeypoints
    .filter(
      (kp) => kp?.keypoint_type === "stat" && (kp?.heading || kp?.description)
    )
    .slice(0, 4);
  const badge = aboutKeypoints.find(
    (kp) => kp?.keypoint_type === "badge" && kp?.heading
  );

  return (
    <div
      className={`nc-SectionHero relative ${className} rounded-md lg:pr-4`}
      data-nc-id="SectionHero"
    >
      <div
        className={
          isCenter
            ? "flex flex-col space-y-14 items-center relative text-center"
            : "flex flex-col lg:flex-row space-y-14 lg:space-y-0 items-center relative text-center lg:text-left lg:justify-between"
        }
      >
        {isUsableUrl(image_url) && (
          <div
            className={`flex-grow${
              isCenter ? "" : isRight ? " lg:order-2" : ""
            }`}
          >
            {/* Relative wrapper so the floating badge can pin to the image
                corner (and matches the image's alignment + size). */}
            <div
              className={`relative ${
                !width && !height ? "w-full max-w-[520px]" : "inline-block"
              } ${isRight ? "ml-auto" : isLeft ? "mr-auto" : "mx-auto"}`}
            >
              <img
                className={`object-cover rounded-2xl ${
                  !width && !height ? "w-full h-auto aspect-[4/3]" : ""
                }`}
                src={image_url}
                alt=""
                style={imgStyle}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                  setImgLoaded(false);
                }}
              />
              {/* Floating badge — single about-us keypoint with
                  keypoint_type="badge", heading=label. Pinned to the
                  image's top-left corner. Hidden when not set. */}
              {badge && (
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-bg-color/90 backdrop-blur-md px-4 py-2 ring-1 ring-text-color/10 shadow-lg">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-primary-200 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-200" />
                  </span>
                  <span
                    className="text-[11px] font-semibold tracking-wider uppercase text-text-color [&_p]:m-0 [&_p]:inline"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(badge.heading) }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        <div
          className={
            "w-full max-w-full text-center" +
            (image_url && !isCenter ? " lg:text-left w-1/2 " : "") +
            (isRight ? " lg:order-1 " : "") +
            (imgLoaded ? " mx-10 " : "")
          }
        >
        <h2 className="mb-4 font-semibold text-2xl text-text-color leading-none"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml((heading)) }}
        />
          <span className="block !font-normal text-2xl leading-none text-secondary-text-color break-words text-wrap">
            {expanded || !isTruncated ? (
              <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }} />
            ) : (
              <>
                <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(shortDescription) }} />
                ...
              </>
            )}
            {isTruncated && (
              <button
                type="button"
                className="ml-2 text-primary font-semibold focus:outline-none inline cursor-pointer"
                onClick={() => setExpanded((v) => !v)}
                style={{ display: "inline" }}
              >
                {expanded ? "View less" : "View more"}
              </button>
            )}
          </span>

          {/* Stat chips — numeric proof points below the About Us copy. Each
              is one about-us keypoint with keypoint_type="stat"
              (heading=value, description=label). Hidden when none configured. */}
          {stats.length > 0 && (
            <div
              className={`grid gap-3 sm:gap-4 mt-7 ${
                stats.length === 1
                  ? "grid-cols-1"
                  : stats.length === 2
                  ? "grid-cols-2"
                  : stats.length === 4
                  ? "grid-cols-2 sm:grid-cols-4"
                  : "grid-cols-3"
              }`}
            >
              {stats.map((s: any, i: number) => (
                <div
                  key={s.id ?? i}
                  className="rounded-2xl bg-secondary-50/70 border border-secondary-100 px-3 py-3 text-center"
                >
                  <div
                    className="text-xl sm:text-2xl font-extrabold text-text-color leading-none [&_p]:m-0 [&_p]:inline"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(s.heading || "") }}
                  />
                  <div
                    className="mt-1 text-[10px] sm:text-xs uppercase tracking-wider text-secondary-text-color font-semibold [&_p]:m-0 [&_p]:inline"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(s.description || "") }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionAbout;
